import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('');
  const lastUpdated = 'December 1, 2025';

  const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'information-collection', title: 'Information We Collect' },
    { id: 'information-usage', title: 'How We Use Your Information' },
    { id: 'information-sharing', title: 'Information Sharing & Disclosure' },
    { id: 'data-security', title: 'Data Security' },
    { id: 'data-retention', title: 'Data Retention' },
    { id: 'your-rights', title: 'Your Privacy Rights' },
    { id: 'cookies', title: 'Cookies & Tracking' },
    { id: 'international', title: 'International Data Transfers' },
    { id: 'children', title: 'Children\'s Privacy' },
    { id: 'changes', title: 'Changes to This Policy' },
    { id: 'contact', title: 'Contact Us' }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  return (
    <>
      <SEO 
        title="Privacy Policy - Data Protection & Privacy Commitment"
        description="Learn how Abacies Logiciels collects, uses, and protects your personal information. Our commitment to data privacy, security, and compliance with GDPR and other regulations."
        keywords="privacy policy, data protection, GDPR, data security, personal information, privacy rights"
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Privacy <span className="text-gradient">Policy</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed animate-slide-up mb-4">
              Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
            </p>
            <p className="text-sm text-cool-grey">
              Last Updated: {lastUpdated}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="card mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-charcoal">Quick Navigation</h3>
                  <nav className="space-y-2">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-300 ${
                          activeSection === section.id
                            ? 'bg-primary-red text-white'
                            : 'text-titanium hover:bg-alabaster hover:text-primary-red'
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Quick Links */}
                <div className="card bg-alabaster">
                  <h3 className="text-lg font-semibold mb-4 text-charcoal">Related Policies</h3>
                  <div className="space-y-2">
                    <Link to="/legal/terms" className="block text-primary-red hover:underline text-sm">
                      Terms of Service →
                    </Link>
                    <Link to="/legal/cookies" className="block text-primary-red hover:underline text-sm">
                      Cookie Policy →
                    </Link>
                    <Link to="/legal/gdpr" className="block text-primary-red hover:underline text-sm">
                      GDPR Compliance →
                    </Link>
                    <Link to="/legal/data-security" className="block text-primary-red hover:underline text-sm">
                      Data Security →
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
              <div className="max-w-3xl space-y-12">
                {/* Overview */}
                <section id="overview">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">Overview</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      At Abacies Logiciels ("we," "our," or "us"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or interact with us.
                    </p>
                    <p>
                      By accessing or using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
                    </p>
                    <div className="bg-blush-light/20 border-l-4 border-primary-red p-4 rounded-r-lg">
                      <p className="font-semibold text-charcoal mb-2">Important Note:</p>
                      <p className="text-sm">
                        This policy applies to information we collect through our website, applications, and services. It does not apply to information collected by third parties, including through any third-party websites or applications that may link to or be accessible from our services.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Information We Collect */}
                <section id="information-collection">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">Information We Collect</h2>
                  <div className="space-y-6 text-titanium">
                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-charcoal">Personal Information You Provide</h3>
                      <p className="mb-3">We collect information that you voluntarily provide to us when you:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Register for an account or fill out forms on our website</li>
                        <li>Request information about our services</li>
                        <li>Subscribe to our newsletter or marketing communications</li>
                        <li>Contact us for customer support</li>
                        <li>Participate in surveys, contests, or promotions</li>
                        <li>Apply for employment opportunities</li>
                      </ul>
                      <p className="mt-3">This information may include:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                        <li>Name, email address, phone number</li>
                        <li>Company name, job title, business information</li>
                        <li>Billing and payment information</li>
                        <li>Professional credentials and work history</li>
                        <li>Communications preferences</li>
                        <li>Any other information you choose to provide</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-charcoal">Information Collected Automatically</h3>
                      <p className="mb-3">When you access our services, we automatically collect certain information, including:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
                        <li><strong>Usage Data:</strong> Pages visited, time spent on pages, links clicked, referral sources</li>
                        <li><strong>Location Data:</strong> General geographic location based on IP address</li>
                        <li><strong>Cookies & Similar Technologies:</strong> Session data, preferences, analytics information</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-charcoal">Information from Third Parties</h3>
                      <p>We may receive information about you from third-party sources, including:</p>
                      <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                        <li>Business partners and service providers</li>
                        <li>Social media platforms (if you choose to connect them)</li>
                        <li>Public databases and data aggregators</li>
                        <li>Marketing and analytics providers</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* How We Use Your Information */}
                <section id="information-usage">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">How We Use Your Information</h2>
                  <div className="space-y-4 text-titanium">
                    <p>We use the information we collect for various purposes, including:</p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="card bg-alabaster">
                        <h4 className="font-semibold text-charcoal mb-2">Service Delivery</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Provide and maintain our services</li>
                          <li>• Process transactions and payments</li>
                          <li>• Deliver customer support</li>
                          <li>• Manage your account</li>
                        </ul>
                      </div>
                      <div className="card bg-alabaster">
                        <h4 className="font-semibold text-charcoal mb-2">Communication</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Send service-related notifications</li>
                          <li>• Respond to inquiries and requests</li>
                          <li>• Provide updates and announcements</li>
                          <li>• Send marketing communications (with consent)</li>
                        </ul>
                      </div>
                      <div className="card bg-alabaster">
                        <h4 className="font-semibold text-charcoal mb-2">Improvement & Analytics</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Analyze usage patterns and trends</li>
                          <li>• Improve our products and services</li>
                          <li>• Conduct research and development</li>
                          <li>• Personalize user experience</li>
                        </ul>
                      </div>
                      <div className="card bg-alabaster">
                        <h4 className="font-semibold text-charcoal mb-2">Legal & Security</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Comply with legal obligations</li>
                          <li>• Protect against fraud and abuse</li>
                          <li>• Enforce our terms and policies</li>
                          <li>• Resolve disputes</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-alabaster p-4 rounded-lg mt-6">
                      <h4 className="font-semibold text-charcoal mb-2">Legal Basis for Processing (GDPR)</h4>
                      <p className="text-sm">
                        For users in the European Economic Area (EEA), we process your personal data based on:
                      </p>
                      <ul className="text-sm space-y-1 mt-2 ml-4">
                        <li>• <strong>Consent:</strong> You have given clear consent for specific purposes</li>
                        <li>• <strong>Contract:</strong> Processing is necessary for contract performance</li>
                        <li>• <strong>Legal Obligation:</strong> We must comply with legal requirements</li>
                        <li>• <strong>Legitimate Interests:</strong> Processing serves our legitimate business interests</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Information Sharing */}
                <section id="information-sharing">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">Information Sharing & Disclosure</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      We do not sell, rent, or trade your personal information. We may share your information in the following circumstances:
                    </p>

                    <div className="space-y-4">
                      <div className="border-l-4 border-primary-red pl-4">
                        <h4 className="font-semibold text-charcoal mb-2">Service Providers</h4>
                        <p className="text-sm">
                          We share information with trusted third-party service providers who assist us in operating our business, such as cloud hosting, payment processing, analytics, marketing, and customer support. These providers are contractually obligated to protect your information.
                        </p>
                      </div>

                      <div className="border-l-4 border-primary-red pl-4">
                        <h4 className="font-semibold text-charcoal mb-2">Business Transfers</h4>
                        <p className="text-sm">
                          If we are involved in a merger, acquisition, sale of assets, or bankruptcy, your information may be transferred as part of that transaction. We will notify you before your information becomes subject to a different privacy policy.
                        </p>
                      </div>

                      <div className="border-l-4 border-primary-red pl-4">
                        <h4 className="font-semibold text-charcoal mb-2">Legal Requirements</h4>
                        <p className="text-sm">
                          We may disclose your information if required by law, regulation, legal process, or governmental request, or to protect our rights, property, safety, or the rights of others.
                        </p>
                      </div>

                      <div className="border-l-4 border-primary-red pl-4">
                        <h4 className="font-semibold text-charcoal mb-2">With Your Consent</h4>
                        <p className="text-sm">
                          We may share your information with third parties when you have given us explicit consent to do so.
                        </p>
                      </div>

                      <div className="border-l-4 border-primary-red pl-4">
                        <h4 className="font-semibold text-charcoal mb-2">Aggregated or De-identified Data</h4>
                        <p className="text-sm">
                          We may share aggregated or de-identified information that cannot reasonably be used to identify you.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Data Security */}
                <section id="data-security">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">Data Security</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                    </p>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="card">
                        <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-3">
                          <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-charcoal mb-2">Encryption</h4>
                        <p className="text-sm">
                          SSL/TLS encryption for data in transit and encryption at rest for sensitive data
                        </p>
                      </div>

                      <div className="card">
                        <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-3">
                          <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-charcoal mb-2">Access Controls</h4>
                        <p className="text-sm">
                          Role-based access control and multi-factor authentication for authorized personnel
                        </p>
                      </div>

                      <div className="card">
                        <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-3">
                          <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-charcoal mb-2">Regular Audits</h4>
                        <p className="text-sm">
                          Regular security assessments, penetration testing, and compliance audits
                        </p>
                      </div>

                      <div className="card">
                        <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-3">
                          <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <h4 className="font-semibold text-charcoal mb-2">Incident Response</h4>
                        <p className="text-sm">
                          Comprehensive incident response plan and 24/7 security monitoring
                        </p>
                      </div>
                    </div>

                    <div className="bg-coral-mist/10 border border-coral-mist/30 p-4 rounded-lg">
                      <p className="text-sm">
                        <strong className="text-charcoal">Important:</strong> While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security, but we continuously work to maintain the security of your data.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Data Retention */}
                <section id="data-retention">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">Data Retention</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
                    </p>
                    <p>
                      Our retention periods are based on:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>The length of time we have an ongoing relationship with you</li>
                      <li>Whether we have a legal obligation to retain the data</li>
                      <li>Whether retention is advisable given our legal position (e.g., statutes of limitations, litigation, or regulatory investigations)</li>
                    </ul>
                    <p className="mt-4">
                      When we no longer need your information, we will securely delete or anonymize it. If deletion is not possible (for example, because information has been stored in backup archives), we will securely store your information and isolate it from further processing until deletion is possible.
                    </p>
                  </div>
                </section>

                {/* Your Rights */}
                <section id="your-rights">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">Your Privacy Rights</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      Depending on your location, you may have certain rights regarding your personal information:
                    </p>

                    <div className="space-y-3">
                      {[
                        {
                          title: 'Right to Access',
                          description: 'Request copies of your personal information and understand how we use it'
                        },
                        {
                          title: 'Right to Rectification',
                          description: 'Request correction of inaccurate or incomplete information'
                        },
                        {
                          title: 'Right to Erasure',
                          description: 'Request deletion of your personal information under certain circumstances'
                        },
                        {
                          title: 'Right to Restrict Processing',
                          description: 'Request limitation on how we process your information'
                        },
                        {
                          title: 'Right to Data Portability',
                          description: 'Request transfer of your information to another service provider'
                        },
                        {
                          title: 'Right to Object',
                          description: 'Object to processing of your information for certain purposes'
                        },
                        {
                          title: 'Right to Withdraw Consent',
                          description: 'Withdraw consent where processing is based on your consent'
                        },
                        {
                          title: 'Right to Lodge a Complaint',
                          description: 'File a complaint with your local data protection authority'
                        }
                      ].map((right, index) => (
                        <div key={index} className="card bg-alabaster">
                          <h4 className="font-semibold text-charcoal mb-1">{right.title}</h4>
                          <p className="text-sm">{right.description}</p>
                        </div>
                      ))}
                    </div>

                    <div className="bg-blush-light/20 border-l-4 border-primary-red p-4 rounded-r-lg mt-6">
                      <h4 className="font-semibold text-charcoal mb-2">Exercising Your Rights</h4>
                      <p className="text-sm mb-2">
                        To exercise any of these rights, please contact us at <a href="mailto:privacy@abacies.com" className="text-primary-red hover:underline">privacy@abacies.com</a>. We will respond to your request within 30 days.
                      </p>
                      <p className="text-sm">
                        We may ask you to verify your identity before processing your request to ensure the security of your information.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Cookies */}
                <section id="cookies">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">Cookies & Tracking Technologies</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      We use cookies and similar tracking technologies to collect and track information about your browsing activities. You can control cookies through your browser settings.
                    </p>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="card">
                        <h4 className="font-semibold text-charcoal mb-2">Essential Cookies</h4>
                        <p className="text-sm">Required for basic site functionality and security</p>
                      </div>
                      <div className="card">
                        <h4 className="font-semibold text-charcoal mb-2">Analytics Cookies</h4>
                        <p className="text-sm">Help us understand how visitors use our website</p>
                      </div>
                      <div className="card">
                        <h4 className="font-semibold text-charcoal mb-2">Marketing Cookies</h4>
                        <p className="text-sm">Used to deliver relevant advertisements</p>
                      </div>
                    </div>

                    <p className="text-sm">
                      For more detailed information about our use of cookies, please see our{' '}
                      <Link to="/legal/cookies" className="text-primary-red hover:underline">
                        Cookie Policy
                      </Link>
                      .
                    </p>
                  </div>
                </section>

                {/* International Transfers */}
                <section id="international">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">International Data Transfers</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from the laws of your country.
                    </p>
                    <p>
                      When we transfer your information internationally, we ensure appropriate safeguards are in place, including:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Standard Contractual Clauses approved by the European Commission</li>
                      <li>Adequacy decisions by relevant authorities</li>
                      <li>Privacy Shield Framework (where applicable)</li>
                      <li>Binding Corporate Rules</li>
                    </ul>
                  </div>
                </section>

                {/* Children's Privacy */}
                <section id="children">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">Children's Privacy</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
                    </p>
                    <p>
                      If we become aware that we have collected personal information from a child under 18 without parental consent, we will take steps to delete that information from our servers.
                    </p>
                  </div>
                </section>

                {/* Changes to Policy */}
                <section id="changes">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">Changes to This Privacy Policy</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Posting the updated policy on this page</li>
                      <li>Updating the "Last Updated" date at the top of this policy</li>
                      <li>Sending you an email notification (for significant changes)</li>
                      <li>Displaying a prominent notice on our website</li>
                    </ul>
                    <p className="mt-4">
                      We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information. Your continued use of our services after any changes indicates your acceptance of the updated policy.
                    </p>
                  </div>
                </section>

                {/* Contact */}
                <section id="contact">
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">Contact Us</h2>
                  <div className="space-y-4 text-titanium">
                    <p>
                      If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                    </p>

                    <div className="card bg-alabaster">
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-charcoal mb-2">Privacy Team</h4>
                          <p className="text-sm">Abacies Logiciels</p>
                          <p className="text-sm">123 Technology Drive</p>
                          <p className="text-sm">Silicon Valley, CA 94025</p>
                          <p className="text-sm">United States</p>
                        </div>
                        <div>
                          <p className="text-sm">
                            <strong>Email:</strong>{' '}
                            <a href="mailto:privacy@abacies.com" className="text-primary-red hover:underline">
                              privacy@abacies.com
                            </a>
                          </p>
                          <p className="text-sm">
                            <strong>Phone:</strong> +1 (555) 123-4567
                          </p>
                        </div>
                        <div>
                          <p className="text-sm">
                            <strong>Data Protection Officer:</strong>{' '}
                            <a href="mailto:dpo@abacies.com" className="text-primary-red hover:underline">
                              dpo@abacies.com
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>

                    <p className="text-sm">
                      We aim to respond to all legitimate requests within 30 days. Occasionally it may take us longer if your request is particularly complex or you have made multiple requests.
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-alabaster">
        <div className="page-container text-center">
          <h2 className="text-3xl font-bold mb-4 text-charcoal">
            Have Questions About Your Privacy?
          </h2>
          <p className="text-xl text-titanium mb-8 max-w-2xl mx-auto">
            Our privacy team is here to help. Contact us for any questions about how we handle your data.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Contact Privacy Team
            </Link>
            <Link to="/legal/gdpr" className="btn-secondary">
              GDPR Information
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;